import React from 'react';
import {
  getHeroContent, HeroImage, StoreLogo, SearchBar, CartBtn, TabContent, StoreFooter, FaHeart, getTabLabel, InlineEditableText
} from './ThemeLayoutHelpers';

export default function BreezeLayout({ p }) {
  const T = p.storeTheme || {};
  const primary = T.primaryColor || "#06b6d4";
  const text = p.storefrontText || {};
  const copy = getHeroContent(p, {
    eyebrow: "Breeze • Organic Lifestyle",
    title: "Lightness For Your Every Day.",
    subtitle: "Carefully sourced lifestyle selections matching clean materials with coastal design.",
    primaryCta: "Discover Shop",
    secondaryCta: "Read Story",
  });

  return (
    <div className="min-h-screen flex flex-col bg-[#fafdfd]" style={{ fontFamily: T.fontFamily || "DM Sans, sans-serif" }}>
      {/* Decorative subtle top waves border or gradient */}
      <div className="absolute top-0 inset-x-0 h-40 bg-gradient-to-b from-cyan-100/40 to-transparent pointer-events-none" />

      {/* Coastal Navigation Bar */}
      <nav className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-cyan-100/60 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <StoreLogo storeInfo={p.storeInfo} border="border border-cyan-200" />
            <div>
              <p className="text-sm font-extrabold text-cyan-950 tracking-tight">{p.storeInfo?.businessName}</p>
              <p className="text-[10px] text-cyan-500 uppercase tracking-widest font-bold mt-0.5">{p.storeInfo?.businessCategory || "Lifestyle"}</p>
            </div>
          </div>
          <SearchBar searchQuery={p.searchQuery} setSearchQuery={p.setSearchQuery} theme={T} className="flex-1 hidden md:block max-w-md" />
          <div className="flex items-center gap-3">
            <button onClick={p.handleGoToWishlist} className="relative p-2.5 rounded-2xl border border-cyan-100 hover:bg-cyan-50 bg-white/50 text-cyan-700 transition">
              <FaHeart className="text-base" />
              {p.wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 h-5 w-5 text-[9px] rounded-full text-white flex items-center justify-center font-bold" style={{ backgroundColor: primary }}>
                  {p.wishlistCount}
                </span>
              )}
            </button>
            <CartBtn p={p} />
            {p.userMenuElement}
          </div>
        </div>
      </nav>

      {/* Hero and Lifestyle Showcase Split layout */}
      <section className="max-w-7xl mx-auto w-full px-6 pt-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_0.7fr] gap-6">
          {/* Main Hero Banner with Rounded Corners */}
          <div className="rounded-[2.5rem] overflow-hidden shadow-xl border border-cyan-100/50 relative">
            <HeroImage p={p} themeId="breeze" className="h-[360px] md:h-[420px]">
              <div className="absolute inset-0 bg-gradient-to-t from-cyan-950/80 via-cyan-900/30 to-transparent pointer-events-none" />
              <div className="absolute bottom-0 inset-x-0 p-8 text-white">
                <span className="text-[10px] uppercase tracking-[0.25em] font-extrabold text-cyan-200/90">
                  <InlineEditableText p={p} field="heroEyebrow" value={copy.eyebrow}>{copy.eyebrow}</InlineEditableText>
                </span>
                <h1 className="text-3xl md:text-4xl font-extrabold mt-2 leading-tight">
                  <InlineEditableText p={p} field="heroTitle" value={copy.title}>{copy.title}</InlineEditableText>
                </h1>
                <p className="text-cyan-50/80 mt-3 text-sm md:text-base max-w-xl leading-relaxed">
                  <InlineEditableText p={p} field="heroSubtitle" value={copy.subtitle}>{copy.subtitle}</InlineEditableText>
                </p>
                <div className="mt-6 flex gap-3">
                  <button
                    onClick={() => p.setActiveTab("products")}
                    className="px-6 py-3 rounded-2xl text-xs font-bold text-white shadow-md hover:brightness-105 transition"
                    style={{ backgroundColor: primary }}
                  >
                    <InlineEditableText p={p} field="primaryCtaText" value={copy.primaryCta}>{copy.primaryCta}</InlineEditableText>
                  </button>
                </div>
              </div>
            </HeroImage>
          </div>

          {/* Quick Summary card */}
          <div className="flex flex-col gap-4">
            <div className="rounded-[2rem] border border-cyan-100 bg-white p-6 shadow-sm flex-1 flex flex-col justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-cyan-800 font-bold mb-4">Store Highlights</p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 rounded-2xl bg-cyan-50/50 border border-cyan-100/50">
                    <span className="text-2xl">🌊</span>
                    <div>
                      <p className="text-xs font-bold text-cyan-950">Coastal Aesthetic</p>
                      <p className="text-[10px] text-cyan-600/80 font-medium">Breezy beach-inspired design</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-2xl bg-cyan-50/50 border border-cyan-100/50">
                    <span className="text-2xl">🌱</span>
                    <div>
                      <p className="text-xs font-bold text-cyan-950">Ethically Curated</p>
                      <p className="text-[10px] text-cyan-600/80 font-medium">Eco-friendly and natural products</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-6">
                <button
                  onClick={() => p.setActiveTab("products")}
                  className="w-full py-3.5 rounded-2xl text-xs font-bold text-white transition hover:opacity-95"
                  style={{ backgroundColor: primary }}
                >
                  {text.shopNow || "Explore Products"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation tabs */}
      <section className="max-w-7xl mx-auto w-full px-6 mt-8">
        <div className="rounded-2xl border border-cyan-100/60 bg-white/70 backdrop-blur-sm p-1.5 flex gap-1.5 overflow-x-auto shadow-sm">
          {[
            ["products", getTabLabel(p, "products")],
            ["services", getTabLabel(p, "services")],
            ["details", getTabLabel(p, "details")]
          ].map(([k, l]) => (
            <button
              key={k}
              onClick={() => p.setActiveTab(k)}
              className="px-5 py-2.5 rounded-xl text-xs font-bold whitespace-nowrap transition"
              style={p.activeTab === k ? { backgroundColor: primary, color: "#fff" } : { color: "#164e63" }}
            >
              {l}
            </button>
          ))}
        </div>
      </section>

      {/* Main product catalogue */}
      <section className="flex-1 max-w-7xl mx-auto w-full px-6 py-6">
        <TabContent p={p} />
      </section>

      <StoreFooter p={p} />
    </div>
  );
}
